import { CheckCircleOutline, DoNotDisturb, OpenInNew } from "@mui/icons-material";
import { Button, IconButton, Stack, Typography } from "@mui/material";
import { DataType, SortDirection } from "ka-table";
import { Column } from "ka-table/models";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import CrmTable from "../App/Components/Table/CrmTable";
import { useImportConflicts, useResolveConflict } from "../App/Queries/ImportQueries";
import { useDebouncedHandler } from "../App/State/Hooks/useDebouncedHandler";
import { useImportTabStore } from "../App/State/Stores/ImportTabStore";
import { IImportConflict } from "../App/Typings/Imports";
import { formatPhone } from "../App/Utilities/formatUtilities";
import {
  AttachmentsPopover,
  ImportSearchBar,
  MaxSearchLength,
  paginationOptions,
  renderTablePaginationDisplay,
} from "./Components/SharedTableElements";

const columns: Column[] = [
  {
    title: "Row Number",
    key: "rowNumber",
    dataType: DataType.Number,
    sortDirection: SortDirection.Ascend,
    width: 140,
  },
  { title: "Import Name", key: "name", dataType: DataType.String },
  { title: "Candidate", key: "candidateId", dataType: DataType.String, isSortable: false },
  { title: "Matched Email", key: "email", dataType: DataType.String },
  { title: "Matched Phone", key: "phone", dataType: DataType.String },
  { title: "Description", key: "reason", dataType: DataType.String },
  { title: "Attachments", key: "attachments", dataType: DataType.Object, isSortable: false },
  { title: "Status", key: "status", dataType: DataType.String },
  {
    title: "Resolve",
    key: "resolve",
    dataType: DataType.String,
    isEditable: false,
    isSortable: false,
    isFilterable: false,
  },
];

export function ConflictsTab({ statusId }: { statusId: string }) {
  const [searchInput, setSearchInput] = useState<string>("");
  const [searchInputError, setSearchInputError] = useState<string>("");
  const {
    query: { search, sortBy, sortDirection, page, pageSize },
    includeResolved,
    selectAll,
    selectedConflicts,
    addSelectedConflict,
    removeSelectedConflict,
    setSelectAll,
    setSelectedConflicts,
    setPage,
    setSearch,
    handleRequestSort,
    handleChangePage,
    handleChangePageSize,
    toQueryString,
  } = useImportTabStore(
    useShallow((state) => ({
      query: state.query,
      includeResolved: state.includeResolved,
      selectAll: state.selectAll,
      selectedConflicts: state.selectedConflicts,
      addSelectedConflict: state.addSelectedConflict,
      removeSelectedConflict: state.removeSelectedConflict,
      setSelectAll: state.setSelectAll,
      setSelectedConflicts: state.setSelectedConflicts,
      setPage: state.setPage,
      setSearch: state.setSearch,
      handleRequestSort: state.handleRequestSort,
      handleChangePage: state.handleChangePage,
      handleChangePageSize: state.handleChangePageSize,
      toQueryString: state.toQueryString,
    })),
  );

  const [queryString, setQueryString] = useState<string>("");
  const { data: conflicts, isLoading } = useImportConflicts(statusId, queryString);

  const itemCount = conflicts?.data.items ? conflicts.data.items.length : 0;
  const totalCount = conflicts?.data.totalItems ?? 0;
  const totalCountDisplay = totalCount > pageSize ? totalCount : itemCount;

  const handleSearchFilter = useDebouncedHandler((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length > MaxSearchLength) {
      setSearchInputError(`Search input must be less than or equal to ${MaxSearchLength} characters`);
    } else {
      setSearchInputError("");
    }
    setSearchInput(e.target.value);
  }, 300);

  const isSelected = (conflictId: string) => selectedConflicts.some((c) => c === conflictId);

  const handleChangeSortBy = (sortBy: string) => {
    let sortProperty = "rowNumber";
    switch (sortBy) {
      case "changeSource":
        sortProperty = "type";
        break;
      case "rowNumber":
      case "dateChanged":
      case "name":
        sortProperty = sortBy;
    }
    handleRequestSort(sortProperty);
  };

  useEffect(() => {
    if (searchInput.length <= MaxSearchLength && searchInput !== search) {
      setSearch(searchInput);
      setPage(0);
    }
  }, [searchInput, search, searchInputError, setPage, setSearch]);

  useEffect(() => {
    setQueryString(toQueryString("conflicts"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, sortBy, sortDirection, search, selectAll, includeResolved]);

  useEffect(() => {
    if (selectAll && conflicts) {
      setSelectedConflicts(conflicts.selected);
    }
  }, [selectAll, conflicts, setSelectedConflicts]);

  return (
    <>
      <ImportSearchBar
        label="Filter Conflicts"
        placeholder="Search by row number, name, email, or phone"
        defaultValue={search}
        error={searchInputError.length > 0}
        errorMessage={searchInputError}
        dataTestId="import-conflicts-searchbar"
        onChange={handleSearchFilter}
      />
      <CrmTable
        name="import-conflicts"
        columns={columns}
        loading={isLoading}
        data={conflicts?.data.items ?? []}
        pagination={{
          ...paginationOptions,
          currentPage: page,
          rows: pageSize,
          handleChangePage,
          handleChangeRowsPerPage: handleChangePageSize,
          totalCount: totalCountDisplay,
          labelDisplayedRows: ({ from, to, count }) =>
            renderTablePaginationDisplay({
              from,
              to,
              count,
              isLoading,
              search,
              itemName: "conflicts",
            }),
        }}
        order={sortDirection}
        orderBy={sortBy}
        onSort={handleChangeSortBy}
        rowTestId={(row: IImportConflict) => `row-${row.rowNumber}`}
        isRowDisabled={(row) => row.status !== "Pending"}
        disableSelectAll={!conflicts || conflicts.totalPending === 0}
        isAllSelected={selectAll}
        rowSelection={{
          onRowSelect: (row, selected) => {
            if (row.status === "Pending") {
              if (selected) {
                addSelectedConflict(row.id);
              } else {
                removeSelectedConflict(row.id);
              }
            }
          },
          isRowSelected: (row) => isSelected(row.id),
          onAllSelected: (selected) => {
            if (selected) {
              setSelectAll(true);
            } else {
              setSelectAll(false);
              setSelectedConflicts([]);
            }
          },
        }}
        customCell={{
          elementAttributes: (props) => {
            let title = undefined;
            if (props.column.key === "reason") {
              title = props.rowData.reason;
            }
            if (props.column.key === "email") {
              title = props.rowData.email;
            }
            if (props.column.key === "attachments") {
              title = props.rowData.attachments
                ? (props.rowData.attachments as string[]).join(", ")
                : undefined;
            }
            return {
              title,
              onClick: (_e, extendedEvent) => {
                const { childProps } = extendedEvent;
                if (
                  childProps.column.key !== "actions" &&
                  childProps.column.key !== "selection-cell" &&
                  childProps.column.key !== "tags" &&
                  childProps.rowData.status === "Pending"
                ) {
                  if (childProps.rowData.status === "Pending") {
                    if (isSelected(childProps.rowData.id)) {
                      removeSelectedConflict(childProps.rowData.id);
                    } else {
                      addSelectedConflict(childProps.rowData.id);
                    }
                  }
                }
              },
              "data-test": `item-${props.column.key}-${props.rowData.id}`,
            };
          },
          content: (props) => {
            if (props.column.key === "name") {
              return `${props.rowData.firstName} ${props.rowData.lastName}`;
            }

            if (props.column.key === "candidateId") {
              return (
                <Button
                  component={Link}
                  endIcon={<OpenInNew fontSize="small" />}
                  to={`/candidates/${props.rowData.candidateId}`}
                  target="_blank"
                  sx={{ lineHeight: 1.2 }}
                >
                  View Candidate
                </Button>
              );
            }

            if (props.column.key === "phone" && props.rowData.phone) {
              return (
                <span
                  style={{
                    minWidth: "19ch",
                    display: "block",
                  }}
                >
                  {formatPhone(props.rowData.phone)}
                </span>
              );
            }

            if (props.column.key === "resolve") {
              return <ResolveCell conflict={props.rowData} />;
            }

            if (props.column.key === "attachments") {
              return <AttachmentsPopover attachments={props.rowData.attachments as string[]} />;
            }
          },
        }}
      />
    </>
  );
}

const ResolveCell = ({ conflict }: { conflict: IImportConflict }) => {
  const { mutateAsync: resolveConflicts, isLoading } = useResolveConflict();

  const handleApproveSingleConflict = async (conflict: IImportConflict) => {
    await resolveConflicts({
      importStatusId: conflict.importStatusId,
      approveAll: false,
      rejectAll: false,
      approveIds: [conflict.id],
      rejectIds: [],
    });
  };

  const handleRejectSingleConflict = async (conflict: IImportConflict) => {
    await resolveConflicts({
      importStatusId: conflict.importStatusId,
      approveAll: false,
      rejectAll: false,
      approveIds: [],
      rejectIds: [conflict.id],
    });
  };

  return conflict.status === "Approved" ? (
    <Typography variant="button" sx={{ opacity: 0.3 }}>
      Updating
    </Typography>
  ) : conflict.status !== "Pending" ? (
    <></>
  ) : (
    <Stack direction="row" spacing={1}>
      <IconButton
        color="primary"
        title="Approve Overwrite"
        size="small"
        disabled={isLoading || conflict.status !== "Pending"}
        onClick={(e) => {
          e.stopPropagation();
          if (conflict.status !== "Pending") {
            return;
          }
          handleApproveSingleConflict(conflict);
        }}
      >
        <CheckCircleOutline />
      </IconButton>
      <IconButton
        color="error"
        title="Reject Overwrite"
        size="small"
        disabled={isLoading || conflict.status !== "Pending"}
        onClick={(e) => {
          e.stopPropagation();
          if (conflict.status !== "Pending") {
            return;
          }
          handleRejectSingleConflict(conflict);
        }}
      >
        <DoNotDisturb />
      </IconButton>
    </Stack>
  );
};
